import React, { createContext, useEffect, useState, useRef } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fireEvent } from '../analytics/useForgeUiAnalyticsEvent';
import { useMetricsContext } from '../context';

export type PerformanceAnalyticsValues = {
	eventRef: React.MutableRefObject<{
		productFetch?: {
			requests: Array<{ elapsed: number; fetchDuration: number }>;
		};
		resized?: {
			navigationTimeStart: number;
			resizedValues: Array<{ elapsed: number; height: number }>;
			resizeTotal: number;
		};
	}>;
	hasSent: boolean;
};

export const PerformanceAnalyticsContext = createContext<PerformanceAnalyticsValues>({
	eventRef: { current: {} },
	hasSent: false,
});

export const PerformanceAnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { page: source } = useMetricsContext();

	const eventRef = useRef<PerformanceAnalyticsValues['eventRef']['current']>({});

	const [hasSent, setHasSent] = useState(false);

	useEffect(() => {
		if (hasSent) {
			return;
		}

		// Send all collected performance related analytics in a single event
		const timeout = setTimeout(() => {
			setHasSent(true);

			const eventPayload = structuredClone(eventRef.current);

			fireEvent(createAnalyticsEvent, {
				action: 'performance.profiled',
				actionSubject: 'forge.ui.extension',
				attributes: eventPayload,
				source,
			});
		}, 10000);

		return () => {
			clearTimeout(timeout);
		};
	}, [hasSent, createAnalyticsEvent, source]);

	return (
		<PerformanceAnalyticsContext.Provider value={{ eventRef, hasSent }}>
			{children}
		</PerformanceAnalyticsContext.Provider>
	);
};
